import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  submitPilotApplication,
  PilotApplicationData,
  CompanySize,
  Industry,
  ReferralSource,
  NumberOfLocations,
  COMPANY_SIZE_OPTIONS,
  INDUSTRY_OPTIONS,
  REFERRAL_SOURCE_OPTIONS,
  NUMBER_OF_LOCATIONS_OPTIONS,
} from "@/services/pilotService";

// Design tokens for dark modal background
const colors = {
  primary: "hsl(var(--primary))",
  white: "#FFFFFF",
  whiteSecondary: "rgba(255, 255, 255, 0.7)",
  blueGrotto: "hsl(var(--primary))",
  inputBorder: "rgba(255, 255, 255, 0.3)",
  error: "#f87171",
};

interface PilotContactFormProps {
  onSuccess: (email: string) => void;
  onCancel?: () => void;
  showCancelButton?: boolean;
}

export const PilotContactForm: React.FC<PilotContactFormProps> = ({
  onSuccess,
  onCancel,
  showCancelButton = true,
}) => {
  // Form state
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companySize, setCompanySize] = useState<CompanySize | "">("");
  const [industry, setIndustry] = useState<Industry | "">("");
  const [numberOfLocations, setNumberOfLocations] = useState<NumberOfLocations | "">("");
  const [useCase, setUseCase] = useState("");
  const [referralSource, setReferralSource] = useState<ReferralSource | "">("");

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Validation
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!fullName.trim()) {
      errors.fullName = "Full name is required";
    }

    if (!email.trim()) {
      errors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!companyName.trim()) {
      errors.companyName = "Company name is required";
    }

    if (!companySize) {
      errors.companySize = "Please select your company size";
    }

    if (!industry) {
      errors.industry = "Please select your industry";
    }

    if (!useCase.trim()) {
      errors.useCase = "Please describe your use case";
    }

    if (!numberOfLocations) {
      errors.numberOfLocations = "Please select the number of locations";
    }

    if (!referralSource) {
      errors.referralSource = "Please tell us how you heard about us";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const data: PilotApplicationData = {
        fullName: fullName.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim() || undefined,
        companyName: companyName.trim(),
        companySize: companySize as CompanySize,
        industry: industry as Industry,
        numberOfLocations: numberOfLocations as NumberOfLocations,
        useCase: useCase.trim(),
        referralSource: referralSource as ReferralSource,
      };

      await submitPilotApplication(data);
      onSuccess(email);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit application");
    } finally {
      setIsLoading(false);
    }
  };

  const clearFieldError = (field: string) => {
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const isFormValid =
    fullName.trim() &&
    email.trim() &&
    companyName.trim() &&
    companySize &&
    industry &&
    numberOfLocations &&
    useCase.trim() &&
    referralSource;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Error Message */}
      {error && (
        <div
          role="alert"
          className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-md text-sm"
        >
          {error}
        </div>
      )}

      {/* Full Name */}
      <div className="space-y-2">
        <label
          htmlFor="pilot-fullName"
          className="text-sm font-medium"
          style={{ color: colors.white }}
        >
          Full Name <span style={{ color: colors.error }}>*</span>
        </label>
        <Input
          id="pilot-fullName"
          type="text"
          placeholder="John Smith"
          value={fullName}
          onChange={(e) => {
            setFullName(e.target.value);
            clearFieldError("fullName");
          }}
          disabled={isLoading}
          className="h-10"
          style={{
            borderColor: fieldErrors.fullName ? colors.error : colors.inputBorder,
          }}
        />
        {fieldErrors.fullName && (
          <p className="text-xs" style={{ color: colors.error }}>
            {fieldErrors.fullName}
          </p>
        )}
      </div>

      {/* Email */}
      <div className="space-y-2">
        <label
          htmlFor="pilot-email"
          className="text-sm font-medium"
          style={{ color: colors.white }}
        >
          Email <span style={{ color: colors.error }}>*</span>
        </label>
        <Input
          id="pilot-email"
          type="email"
          placeholder="john@company.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            clearFieldError("email");
          }}
          disabled={isLoading}
          autoComplete="email"
          className="h-10"
          style={{
            borderColor: fieldErrors.email ? colors.error : colors.inputBorder,
          }}
        />
        {fieldErrors.email && (
          <p className="text-xs" style={{ color: colors.error }}>
            {fieldErrors.email}
          </p>
        )}
      </div>

      {/* Phone (Optional) */}
      <div className="space-y-2">
        <label
          htmlFor="pilot-phone"
          className="text-sm font-medium"
          style={{ color: colors.white }}
        >
          Phone Number <span style={{ color: colors.whiteSecondary }}>(optional)</span>
        </label>
        <Input
          id="pilot-phone"
          type="tel"
          placeholder="+1 (555) 123-4567"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={isLoading}
          autoComplete="tel"
          className="h-10"
          style={{ borderColor: colors.inputBorder }}
        />
      </div>

      {/* Company Name */}
      <div className="space-y-2">
        <label
          htmlFor="pilot-companyName"
          className="text-sm font-medium"
          style={{ color: colors.white }}
        >
          Company Name <span style={{ color: colors.error }}>*</span>
        </label>
        <Input
          id="pilot-companyName"
          type="text"
          placeholder="Your Company"
          value={companyName}
          onChange={(e) => {
            setCompanyName(e.target.value);
            clearFieldError("companyName");
          }}
          disabled={isLoading}
          className="h-10"
          style={{
            borderColor: fieldErrors.companyName ? colors.error : colors.inputBorder,
          }}
        />
        {fieldErrors.companyName && (
          <p className="text-xs" style={{ color: colors.error }}>
            {fieldErrors.companyName}
          </p>
        )}
      </div>

      {/* Two columns: Company Size + Industry */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Company Size */}
        <div className="space-y-2">
          <label
            htmlFor="pilot-companySize"
            className="text-sm font-medium"
            style={{ color: colors.white }}
          >
            Company Size <span style={{ color: colors.error }}>*</span>
          </label>
          <Select
            value={companySize}
            onValueChange={(value) => {
              setCompanySize(value as CompanySize);
              clearFieldError("companySize");
            }}
            disabled={isLoading}
          >
            <SelectTrigger
              id="pilot-companySize"
              className="h-10"
              style={{
                borderColor: fieldErrors.companySize ? colors.error : colors.inputBorder,
              }}
            >
              <SelectValue placeholder="Select size" />
            </SelectTrigger>
            <SelectContent>
              {COMPANY_SIZE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {fieldErrors.companySize && (
            <p className="text-xs" style={{ color: colors.error }}>
              {fieldErrors.companySize}
            </p>
          )}
        </div>

        {/* Industry */}
        <div className="space-y-2">
          <label
            htmlFor="pilot-industry"
            className="text-sm font-medium"
            style={{ color: colors.white }}
          >
            Industry <span style={{ color: colors.error }}>*</span>
          </label>
          <Select
            value={industry}
            onValueChange={(value) => {
              setIndustry(value as Industry);
              clearFieldError("industry");
            }}
            disabled={isLoading}
          >
            <SelectTrigger
              id="pilot-industry"
              className="h-10"
              style={{
                borderColor: fieldErrors.industry ? colors.error : colors.inputBorder,
              }}
            >
              <SelectValue placeholder="Select industry" />
            </SelectTrigger>
            <SelectContent>
              {INDUSTRY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {fieldErrors.industry && (
            <p className="text-xs" style={{ color: colors.error }}>
              {fieldErrors.industry}
            </p>
          )}
        </div>
      </div>

      {/* Number of Locations */}
      <div className="space-y-2">
        <label
          htmlFor="pilot-numberOfLocations"
          className="text-sm font-medium"
          style={{ color: colors.white }}
        >
          Number of Locations <span style={{ color: colors.error }}>*</span>
        </label>
        <Select
          value={numberOfLocations}
          onValueChange={(value) => {
            setNumberOfLocations(value as NumberOfLocations);
            clearFieldError("numberOfLocations");
          }}
          disabled={isLoading}
        >
          <SelectTrigger
            id="pilot-numberOfLocations"
            className="h-10"
            style={{
              borderColor: fieldErrors.numberOfLocations ? colors.error : colors.inputBorder,
            }}
          >
            <SelectValue placeholder="Select number of locations" />
          </SelectTrigger>
          <SelectContent>
            {NUMBER_OF_LOCATIONS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {fieldErrors.numberOfLocations && (
          <p className="text-xs" style={{ color: colors.error }}>
            {fieldErrors.numberOfLocations}
          </p>
        )}
      </div>

      {/* Use Case / Business Description */}
      <div className="space-y-2">
        <label
          htmlFor="pilot-useCase"
          className="text-sm font-medium"
          style={{ color: colors.white }}
        >
          How would you use TerraMatrix? <span style={{ color: colors.error }}>*</span>
        </label>
        <textarea
          id="pilot-useCase"
          placeholder="Describe your business needs and how TerraMatrix could help. For example: monitoring equipment sensors, tracking inventory, visualizing production data..."
          value={useCase}
          onChange={(e) => {
            setUseCase(e.target.value);
            clearFieldError("useCase");
          }}
          disabled={isLoading}
          rows={4}
          className="flex w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
          style={{
            borderColor: fieldErrors.useCase ? colors.error : colors.inputBorder,
          }}
        />
        <div className="flex justify-between">
          {fieldErrors.useCase ? (
            <p className="text-xs" style={{ color: colors.error }}>
              {fieldErrors.useCase}
            </p>
          ) : (
            <span />
          )}
          <p className="text-xs" style={{ color: colors.whiteSecondary }}>
            {useCase.length}/2000
          </p>
        </div>
      </div>

      {/* Referral Source */}
      <div className="space-y-2">
        <label
          htmlFor="pilot-referralSource"
          className="text-sm font-medium"
          style={{ color: colors.white }}
        >
          How did you hear about us? <span style={{ color: colors.error }}>*</span>
        </label>
        <Select
          value={referralSource}
          onValueChange={(value) => {
            setReferralSource(value as ReferralSource);
            clearFieldError("referralSource");
          }}
          disabled={isLoading}
        >
          <SelectTrigger
            id="pilot-referralSource"
            className="h-10"
            style={{
              borderColor: fieldErrors.referralSource ? colors.error : colors.inputBorder,
            }}
          >
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent>
            {REFERRAL_SOURCE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {fieldErrors.referralSource && (
          <p className="text-xs" style={{ color: colors.error }}>
            {fieldErrors.referralSource}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className={`flex ${showCancelButton ? "justify-between" : "justify-end"} pt-4 pb-6`}>
        {showCancelButton && onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
            className="h-10"
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          className="h-10 font-medium transition-all duration-200 hover:brightness-95"
          style={{
            backgroundColor: colors.primary,
            color: "#FFFFFF",
          }}
          disabled={isLoading || !isFormValid}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            "Apply for Pilot Program"
          )}
        </Button>
      </div>
    </form>
  );
};
